import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useSite } from "../../lib/useSite";
import { api, apiError, downloadFile } from "../../lib/api";
import { Emblem } from "../../components/PublicLayout";
import { DemoBadge } from "../../components/bits";
import { toast } from "sonner";
import { LogOut, FileText, Calendar, Receipt, MessageSquare, Star, X, FileDown, CreditCard, Repeat, Wallet, Sparkles, Plus, Minus, CalendarCheck, Home, UserCog } from "lucide-react";

const STATUS_COLOR = {
  "Quote Sent": "text-[#d4af37]", "Accepted": "text-green-600", "Enquiry": "text-blue-600",
  "Confirmed": "text-green-600", "Completed": "text-[#0a0a0a]", "Cancelled": "text-red-500",
};

export default function CustomerPortal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const site = useSite();
  const [params] = useSearchParams();
  const [data, setData] = useState({ quotes: [], bookings: [], invoices: [] });
  const [tab, setTab] = useState(params.get("book") ? "book" : "bookings");
  const [msg, setMsg] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [subs, setSubs] = useState(null);
  const [family, setFamily] = useState(null);

  const load = () => api.get("/portal/overview").then(({ data }) => setData(data)).catch(() => {});
  const loadWallet = () => api.get("/portal/wallet").then(({ data }) => setWallet(data)).catch(() => {});
  const loadSubs = () => api.get("/portal/subscriptions").then(({ data }) => setSubs(data)).catch(() => {});
  const loadFamily = () => api.get("/portal/family-status").then(({ data }) => setFamily(data)).catch(() => {});
  useEffect(() => { load(); loadWallet(); loadSubs(); loadFamily(); }, []);

  const bookFamily = async () => {
    const date = window.prompt("Preferred date (YYYY-MM-DD):"); if (!date) return;
    const win = window.prompt("Arrival window (e.g. 10:00–12:00):", "10:00–12:00"); if (!win) return;
    const postcode = window.prompt("Postcode:") || "";
    try { const { data } = await api.post("/portal/family/book", { date, arrival_window: win, postcode }); toast.success(`Family clean booked (${data.reference}) — £${data.price}`); load(); loadFamily(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const buyCredits = async (credits) => {
    try { const { data } = await api.post("/payments/credits-checkout", { credits, origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const requestCleaner = async (b) => {
    if (!window.confirm(`Request a different cleaner for ${b.reference}? Our system will try to re-allocate.`)) return;
    try { const { data } = await api.post(`/portal/bookings/${b.id}/request-cleaner`); toast.success(data.message); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const doSubscribe = async (plan_id) => {
    try {
      const { data } = await api.post("/payments/subscription-checkout", { plan_id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const cancelSub = async (id) => {
    if (!window.confirm("Cancel this membership? It will not renew.")) return;
    try { await api.post(`/portal/subscriptions/${id}/cancel`); toast.success("Membership cancelled"); loadSubs(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const accept = async (id) => {
    try { const { data } = await api.post(`/portal/quotes/${id}/accept`); toast.success(`Booking ${data.booking_reference} created`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const sendMsg = async () => {
    if (!msg.trim()) return;
    try { await api.post("/portal/message", { message: msg }); setMsg(""); toast.success("Message sent to our team"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const submitFeedback = async (rating, comment) => {
    try { await api.post("/portal/feedback", { booking_id: feedback.id, rating, comment }); setFeedback(null); toast.success("Thank you for your feedback"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const openInvoice = async (id) => {
    try { const { data } = await api.get(`/portal/invoices/${id}`); setInvoice(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const cancelBooking = async (b) => {
    if (!window.confirm(`Cancel booking ${b.reference}? Cancellations within 48 hours may incur a charge.`)) return;
    try { const { data } = await api.post(`/portal/bookings/${b.id}/cancel`, { reason: "" }); toast.success(data.message); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const rescheduleBooking = async (b) => {
    const date = window.prompt("New date (YYYY-MM-DD):", b.date || "");
    if (!date) return;
    const win = window.prompt("Arrival window (e.g. 10:00–12:00):", b.arrival_window || "10:00–12:00");
    if (!win) return;
    try { const { data } = await api.post(`/portal/bookings/${b.id}/reschedule`, { date, arrival_window: win }); toast.success(data.message); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const TABS = [["book", "Book a Clean", CalendarCheck], ["bookings", "Bookings", Calendar], ["quotes", "Quotes", FileText], ["specialist", "Specialist", Sparkles], ["invoices", "Invoices", Receipt], ["credits", "Credits", Wallet], ["subscriptions", "Membership", Repeat], ["properties", "Properties", Home], ["messages", "Messages", MessageSquare]];

  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      <header className="bg-[#0a0a0a] border-b border-[#333] sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <Emblem light />
          <div className="flex items-center gap-4">
            <span className="text-[#a3a3a3] text-sm hidden sm:inline">{user?.name}</span>
            <button data-testid="portal-logout" onClick={() => { logout(); nav("/"); }} className="text-[#a3a3a3] hover:text-[#d4af37] flex items-center gap-1 text-sm"><LogOut size={16} /> Sign out</button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 py-8">
        <h1 className="font-display text-4xl tracking-tight">Welcome, {user?.name?.split(" ")[0]}</h1>
        <p className="text-[#555] text-sm mt-1">Manage your quotes, bookings and invoices.</p>

        {family?.eligible && (
          <div className="mt-5 card-luxe p-5 flex items-center justify-between flex-wrap gap-3 border-l-4 border-[#d4af37]" data-testid="family-offer">
            <div>
              <p className="text-[11px] tracking-luxe uppercase text-[#b5952f]">Your private rate</p>
              <p className="font-display text-xl mt-1">3-hour Essential clean — £{family.rate}</p>
              <p className="text-[#8a8a8a] text-sm">A personal rate arranged for you. {family.cleans_used} used so far.</p>
            </div>
            <button data-testid="family-book-btn" onClick={bookFamily} className="btn-gold px-6 py-2.5 rounded-sm text-sm">Book £{family.rate} clean</button>
          </div>
        )}

        <div className="flex gap-2 mt-6 border-b border-[#e0ddd4] overflow-x-auto">
          {TABS.map(([k, label, Icon]) => (
            <button key={k} data-testid={`portal-tab-${k}`} onClick={() => setTab(k)}
              className={`flex items-center gap-2 px-4 py-3 text-sm whitespace-nowrap border-b-2 -mb-px ${tab === k ? "border-[#d4af37] text-[#0a0a0a] font-semibold" : "border-transparent text-[#8a8a8a]"}`}>
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "quotes" && (
            <div className="space-y-4">
              {data.quotes.length === 0 && <Empty text="No quotes yet. Request one from the website." />}
              {data.quotes.map((q) => (
                <div key={q.id} className="card-luxe p-6" data-testid={`portal-quote-${q.id}`}>
                  <div className="flex justify-between items-start flex-wrap gap-3">
                    <div>
                      <p className="text-xs text-[#8a8a8a]">{q.reference} {q.demo && <DemoBadge />}</p>
                      <h3 className="font-display text-2xl mt-1">{q.service}</h3>
                      <p className="text-sm text-[#555] mt-1">{q.scope || `${q.property_type || ""} · ${q.postcode || ""}`}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-semibold ${STATUS_COLOR[q.status] || ""}`}>{q.status}</p>
                      {q.quoted_price != null && <p className="font-display text-2xl mt-1">£{q.quoted_price}</p>}
                    </div>
                  </div>
                  {["Quote Sent", "Awaiting Customer"].includes(q.status) && (
                    <button data-testid={`accept-quote-${q.id}`} onClick={() => accept(q.id)} className="btn-gold px-6 py-2.5 rounded-sm text-sm mt-4">Accept quotation</button>
                  )}
                  <button data-testid={`quote-pdf-${q.id}`} onClick={() => downloadFile(`/documents/quote/${q.id}.pdf`, `${q.reference}.pdf`)} className="btn-outline-gold px-4 py-2.5 rounded-sm text-sm mt-4 ml-2 inline-flex items-center gap-2"><FileDown size={14} /> PDF</button>
                </div>
              ))}
            </div>
          )}

          {tab === "bookings" && (
            <div className="space-y-4">
              {data.bookings.length === 0 && <Empty text="No bookings yet." />}
              {data.bookings.map((b) => (
                <div key={b.id} className="card-luxe p-6" data-testid={`portal-booking-${b.id}`}>
                  <div className="flex justify-between flex-wrap gap-3">
                    <div>
                      <p className="text-xs text-[#8a8a8a]">{b.reference}</p>
                      <h3 className="font-display text-2xl mt-1">{b.service}</h3>
                      <p className="text-sm text-[#555] mt-1">{b.date || "Date TBC"} · {b.arrival_window || ""}</p>
                    </div>
                    <p className={`text-sm font-semibold ${STATUS_COLOR[b.status] || "text-[#d4af37]"}`}>{b.status}</p>
                  </div>
                  {b.status === "Completed" && <button onClick={() => setFeedback(b)} className="btn-outline-gold px-4 py-2 rounded-sm text-sm mt-4 inline-flex items-center gap-2"><Star size={14} /> Leave feedback</button>}
                  {!["Completed", "Cancelled", "Cancelled (Late)", "No Access"].includes(b.status) && (
                    <div className="flex gap-2 mt-4 flex-wrap">
                      <button data-testid={`reschedule-${b.id}`} onClick={() => rescheduleBooking(b)} className="btn-outline-gold px-4 py-2 rounded-sm text-sm">Reschedule</button>
                      {b.assigned_staff && <button data-testid={`request-cleaner-${b.id}`} onClick={() => requestCleaner(b)} className="btn-outline-gold px-4 py-2 rounded-sm text-sm">Request different cleaner</button>}
                      <button data-testid={`cancel-booking-${b.id}`} onClick={() => cancelBooking(b)} className="px-4 py-2 rounded-sm text-sm border border-red-300 text-red-500 hover:bg-red-50">Cancel</button>
                    </div>
                  )}
                  {b.assigned_staff && <p className="text-xs text-[#8a8a8a] mt-2">Your cleaner: <span className="text-[#0a0a0a] font-medium">{b.assigned_staff.name}</span></p>}
                  {b.status === "Pending Payment" && <p className="text-xs text-[#b5952f] mt-2">Awaiting payment — complete checkout to confirm.</p>}
                  {["Cancelled (Late)", "No Access"].includes(b.status) && b.cancellation_charge > 0 && (
                    <p className="text-xs text-red-500 mt-3">Late/failed-attendance charge: £{b.cancellation_charge}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === "invoices" && (
            <div className="space-y-4">
              {data.invoices.length === 0 && <Empty text="No invoices yet." />}
              {data.invoices.map((i) => (
                <div key={i.id} className="card-luxe p-6 flex justify-between items-center flex-wrap gap-3" data-testid={`portal-invoice-${i.id}`}>
                  <div>
                    <p className="text-xs text-[#8a8a8a]">{i.invoice_number} {i.demo && <DemoBadge />}</p>
                    <h3 className="font-display text-xl mt-1">{i.service}</h3>
                    <p className="text-sm text-[#555]">Due {i.due_date}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-display text-2xl">£{i.total}</p>
                    <p className={`text-xs ${i.status === "Paid" ? "text-green-600" : "text-[#d4af37]"}`}>{i.status}</p>
                  </div>
                  <button data-testid={`view-invoice-${i.id}`} onClick={() => openInvoice(i.id)} className="btn-outline-gold px-4 py-2 rounded-sm text-sm">View</button>
                </div>
              ))}
            </div>
          )}

          {tab === "credits" && wallet && (
            <div className="space-y-5">
              <div className="bg-[#0a0a0a] text-[#fdfbf7] rounded-sm p-6 flex items-center justify-between flex-wrap gap-4" data-testid="credits-wallet">
                <div><p className="text-[11px] tracking-luxe uppercase text-[#d4af37]">Cleaning Credits balance</p>
                  <p className="font-display text-5xl mt-2">{wallet.credits} <span className="text-lg text-[#a3a3a3]">credits</span></p>
                  <p className="text-[#d4af37] mt-1">= £{wallet.value_gbp} cleaning value (1 credit = £{wallet.credit_value})</p></div>
                <Wallet size={54} className="text-[#d4af37]" />
              </div>
              <div className="card-luxe p-6">
                <h3 className="font-display text-2xl">Buy credits</h3>
                <div className="flex flex-wrap gap-3 mt-4">
                  {wallet.packs.map((p) => (
                    <button key={p} data-testid={`buy-credits-${p}`} onClick={() => buyCredits(p)} className="btn-outline-gold px-5 py-3 rounded-sm text-sm">
                      {p} credits · £{p * wallet.credit_value}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-[#8a8a8a] mt-3">Credits are purchased securely via Stripe (test mode) and can be redeemed toward any booking at checkout. 1 credit = £{wallet.credit_value}.</p>
              </div>
              <div className="card-luxe p-6">
                <h3 className="font-display text-2xl mb-3">Transaction history</h3>
                {wallet.ledger.length === 0 ? <p className="text-[#8a8a8a] text-sm">No transactions yet.</p> :
                  wallet.ledger.map((t) => (
                    <div key={t.id} className="flex justify-between border-b border-[#f0efe9] py-2 text-sm">
                      <span>{t.note}</span>
                      <span className={t.credits < 0 ? "text-red-500" : "text-green-600"}>{t.credits > 0 ? "+" : ""}{t.credits} cr (£{t.value_gbp})</span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {tab === "subscriptions" && subs && (
            <div className="space-y-5">
              {subs.subscriptions.length > 0 && (
                <div className="card-luxe p-6">
                  <h3 className="font-display text-2xl mb-3">Your memberships</h3>
                  {subs.subscriptions.map((s) => (
                    <div key={s.id} className="flex justify-between items-center border-b border-[#f0efe9] py-3" data-testid={`my-sub-${s.id}`}>
                      <div><p className="font-semibold">{s.plan_name} · £{s.price}/4 weeks</p>
                        <p className="text-xs text-[#8a8a8a]">{s.status === "Active" ? `Auto-renews ${s.next_renewal}` : `Cancelled`}</p></div>
                      {s.status === "Active" && <button data-testid={`cancel-sub-${s.id}`} onClick={() => cancelSub(s.id)} className="btn-outline-gold px-4 py-2 rounded-sm text-xs">Cancel</button>}
                    </div>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {subs.plans.map((p) => (
                  <div key={p.id} className="card-luxe p-6 flex flex-col" data-testid={`plan-${p.id}`}>
                    <p className="text-[11px] tracking-luxe uppercase text-[#b5952f]">{p.tier}</p>
                    <h3 className="font-display text-2xl mt-1">{p.name}</h3>
                    <p className="font-display text-3xl mt-2">£{p.price}<span className="text-sm text-[#8a8a8a]"> / 4 weeks</span></p>
                    <p className="text-sm text-[#555] mt-2 flex-1">{p.description}</p>
                    <p className="text-xs text-[#8a8a8a] mt-3">Renews automatically every {p.cycle_weeks} weeks until cancelled. Cancel anytime from your dashboard.</p>
                    <button data-testid={`subscribe-${p.id}`} onClick={() => doSubscribe(p.id)} className="btn-gold px-6 py-2.5 rounded-sm text-sm mt-4">Subscribe</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "book" && <BookingFlow initialTier={params.get("book")} site={site} wallet={wallet} onBooked={() => { load(); loadWallet(); setTab("bookings"); }} />}
          {tab === "properties" && <PropertiesManager />}
          {tab === "specialist" && <SpecialistBasket />}
          {tab === "messages" && (
            <div className="card-luxe p-6 max-w-lg">
              <h3 className="font-display text-2xl">Message our team</h3>
              <textarea data-testid="portal-message-input" rows="4" value={msg} onChange={(e) => setMsg(e.target.value)} className="w-full mt-4 border border-[#e0ddd4] rounded-sm px-4 py-3 text-sm" placeholder="How can we help?" />
              <button data-testid="portal-message-send" onClick={sendMsg} className="btn-gold px-6 py-2.5 rounded-sm text-sm mt-3">Send</button>
            </div>
          )}
        </div>
      </div>

      {feedback && <FeedbackModal onClose={() => setFeedback(null)} onSubmit={submitFeedback} />}
      {invoice && <InvoiceModal invoice={invoice} onClose={() => setInvoice(null)} onPaid={() => { setInvoice(null); load(); loadWallet(); }} />}
    </div>
  );
}

const Empty = ({ text }) => <div className="card-luxe p-10 text-center text-[#8a8a8a]">{text}</div>;

function FeedbackModal({ onClose, onSubmit }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  return (
    <Modal onClose={onClose} title="Leave feedback">
      <div className="flex gap-2 mt-4">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} data-testid={`fb-star-${n}`} onClick={() => setRating(n)}><Star size={28} className={n <= rating ? "text-[#d4af37] fill-[#d4af37]" : "text-[#ccc]"} /></button>
        ))}
      </div>
      <textarea rows="3" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Your comments" className="w-full mt-4 border border-[#e0ddd4] rounded-sm px-4 py-3 text-sm" />
      <button data-testid="fb-submit" onClick={() => onSubmit(rating, comment)} className="btn-gold px-6 py-2.5 rounded-sm text-sm mt-3">Submit</button>
    </Modal>
  );
}

function InvoiceModal({ invoice, onClose, onPaid }) {
  const payCredits = async () => {
    try { const { data } = await api.post(`/invoices/${invoice.id}/pay-credits`); toast.success(`${data.credits_used} credits applied (£${data.value_applied}). ${data.card_due > 0 ? `£${data.card_due} remaining` : "Paid in full"}`); onPaid?.(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const payOnline = async () => {
    try { const { data } = await api.post(`/payments/invoice-checkout`, { invoice_id: invoice.id, origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Modal onClose={onClose} title={`Invoice ${invoice.invoice_number}`} wide>
      <div className="mt-2 text-sm">
        <div className="flex justify-between border-b border-[#eee] pb-3">
          <div><p className="font-semibold">{invoice.customer_name}</p><p className="text-[#8a8a8a]">{invoice.address}</p></div>
          <div className="text-right"><p>Date: {invoice.date}</p><p>Due: {invoice.due_date}</p></div>
        </div>
        <table className="w-full mt-4">
          <thead><tr className="text-left text-[#8a8a8a] text-xs"><th className="py-2">Description</th><th className="text-right">Amount</th></tr></thead>
          <tbody>{(invoice.line_items || []).map((li, i) => <tr key={i} className="border-b border-[#f0f0f0]"><td className="py-2">{li.description}</td><td className="text-right">£{li.amount}</td></tr>)}</tbody>
        </table>
        <div className="mt-4 space-y-1 text-right">
          <p>Subtotal: £{invoice.subtotal}</p>
          {invoice.vat > 0 && <p>VAT: £{invoice.vat}</p>}
          <p className="font-display text-2xl">Total: £{invoice.total}</p>
          <p className="text-xs text-[#8a8a8a]">Paid £{invoice.paid} · Balance £{invoice.balance}</p>
        </div>
        <button onClick={() => window.print()} className="btn-outline-gold px-4 py-2 rounded-sm text-sm mt-4">Print</button>
        <button data-testid="invoice-pdf-download" onClick={() => downloadFile(`/documents/invoice/${invoice.id}.pdf`, `${invoice.invoice_number}.pdf`)} className="btn-gold px-4 py-2 rounded-sm text-sm mt-4 ml-2 inline-flex items-center gap-2"><FileDown size={14} /> Download PDF</button>
        {invoice.balance > 0 && <button data-testid="pay-online" onClick={payOnline} className="btn-gold px-4 py-2 rounded-sm text-sm mt-4 ml-2 inline-flex items-center gap-2"><CreditCard size={14} /> Pay online (card)</button>}
        {invoice.balance > 0 && <button data-testid="pay-with-credits" onClick={payCredits} className="btn-outline-gold px-4 py-2 rounded-sm text-sm mt-4 ml-2 inline-flex items-center gap-2"><Wallet size={14} /> Pay with credits</button>}
      </div>
    </Modal>
  );
}

export function Modal({ children, onClose, title, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose}>
      <div className={`bg-white rounded-sm p-6 w-full ${wide ? "max-w-lg" : "max-w-md"} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center"><h3 className="font-display text-2xl">{title}</h3><button onClick={onClose}><X size={20} /></button></div>
        {children}
      </div>
    </div>
  );
}


function SpecialistBasket() {
  const [cats, setCats] = useState([]);
  const [qty, setQty] = useState({});
  const [quote, setQuote] = useState(null);
  const [slot, setSlot] = useState({ date: "", arrival_window: "10:00–12:00", postcode: "" });
  const WIN = ["08:00–10:00", "10:00–12:00", "12:00–14:00", "14:00–16:00"];
  useEffect(() => { api.get("/specialist/services").then(({ data }) => setCats(data.categories)).catch(() => {}); }, []);
  const items = Object.entries(qty).filter(([, q]) => q > 0).map(([key, q]) => ({ key, quantity: q }));
  useEffect(() => {
    if (items.length === 0) { setQuote(null); return; }
    api.post("/specialist/quote", { items, ...(slot.date ? { date: slot.date, arrival_window: slot.arrival_window, postcode: slot.postcode } : {}) })
      .then(({ data }) => setQuote(data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(qty), slot.date, slot.arrival_window]);
  const bump = (key, d) => setQty((p) => ({ ...p, [key]: Math.max(0, (p[key] || 0) + d) }));
  const book = async () => {
    if (items.length === 0) return toast.error("Add at least one service");
    if (!slot.date) return toast.error("Choose a date");
    try {
      const { data } = await api.post("/portal/specialist/book", { items, date: slot.date, arrival_window: slot.arrival_window, postcode: slot.postcode });
      if (data.requires_quote) { toast.success(`Quotation requested (${data.reference}). We'll confirm your price before any payment.`); return; }
      const { data: co } = await api.post("/payments/specialist-checkout", { booking_id: data.booking_id, origin_url: window.location.origin });
      window.location.href = co.checkout_url;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="specialist-basket">
      <div className="lg:col-span-2 space-y-5">
        {cats.map((c) => (
          <div key={c.category} className="card-luxe p-5">
            <p className="text-[11px] tracking-luxe uppercase text-[#b5952f] mb-3">{c.category}</p>
            <div className="space-y-2">
              {c.items.map((it) => (
                <div key={it.key} className="flex justify-between items-center border-b border-[#f0efe9] pb-2">
                  <div><p className="text-sm">{it.name}</p><p className="text-xs text-[#8a8a8a]">{it.unit === "quote" || it.from_price ? `from £${it.price} · quote` : it.unit === "per_hour" ? `£${it.price}/hr` : `£${it.price}`}</p></div>
                  <div className="flex items-center gap-2">
                    <button data-testid={`spec-minus-${it.key}`} onClick={() => bump(it.key, -1)} className="w-7 h-7 rounded-sm border border-[#e0ddd4] flex items-center justify-center"><Minus size={13} /></button>
                    <span className="w-6 text-center text-sm">{qty[it.key] || 0}</span>
                    <button data-testid={`spec-plus-${it.key}`} onClick={() => bump(it.key, 1)} className="w-7 h-7 rounded-sm border border-[#e0ddd4] flex items-center justify-center"><Plus size={13} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="card-luxe p-5 h-fit sticky top-24" data-testid="specialist-summary">
        <h3 className="font-display text-xl mb-3">Order summary</h3>
        {!quote && <p className="text-[#8a8a8a] text-sm">Add services to see your total.</p>}
        {quote && <>
          <div className="space-y-1.5 mb-3">
            {quote.lines.map((l, i) => <div key={i} className="flex justify-between text-sm"><span>{l.name}{l.quantity > 1 ? ` ×${l.quantity}` : ""}</span><span>{l.amount != null ? `£${l.amount}` : "Quote"}</span></div>)}
          </div>
          <div className="flex justify-between font-semibold border-t border-[#e8e5de] pt-2"><span>{quote.requires_quote ? "Fixed items" : "Total"}</span><span>£{quote.fixed_total}</span></div>
          {quote.requires_quote && <p className="text-xs text-[#b5952f] mt-2">Some items need a quotation — you'll approve the final price before paying.</p>}
          <p className="text-xs text-[#8a8a8a] mt-2">Est. {quote.estimated_hours}h · skills: {quote.required_skills.join(", ") || "—"}</p>
          {quote.availability && !quote.availability.bookable && <p className="text-xs text-red-500 mt-2">No trained/equipped cleaner for this slot{quote.missing_equipment.length ? ` (${quote.missing_equipment.join(", ")})` : ""}.</p>}
        </>}
        <div className="mt-4 space-y-2">
          <input type="date" data-testid="spec-date" value={slot.date} onChange={(e) => setSlot({ ...slot, date: e.target.value })} className="w-full border border-[#e0ddd4] rounded-sm px-3 py-2 text-sm" />
          <select data-testid="spec-window" value={slot.arrival_window} onChange={(e) => setSlot({ ...slot, arrival_window: e.target.value })} className="w-full border border-[#e0ddd4] rounded-sm px-3 py-2 text-sm">{WIN.map((w) => <option key={w}>{w}</option>)}</select>
          <input placeholder="Postcode" data-testid="spec-postcode" value={slot.postcode} onChange={(e) => setSlot({ ...slot, postcode: e.target.value })} className="w-full border border-[#e0ddd4] rounded-sm px-3 py-2 text-sm" />
        </div>
        <button data-testid="spec-book" onClick={book} className="btn-gold w-full py-3 rounded-sm text-sm mt-4">{quote?.requires_quote ? "Request quote & book" : "Book & pay"}</button>
      </div>
    </div>
  );
}


const WINDOWS = ["08:00–10:00", "10:00–12:00", "12:00–14:00", "14:00–16:00"];
const FREQS = ["One-off", "Weekly", "Fortnightly", "Monthly"];
const TIER_META = {
  "Essential": { rate: 25, min: 3, skill: "Domestic" },
  "Premium": { rate: 35, min: 3, skill: "Premium" },
  "Elite Signature": { rate: 50, min: 4, skill: "Elite" },
};

function BookingFlow({ initialTier, site, wallet, onBooked }) {
  const tiers = (site?.packages || []).filter((p) => p.type === "hourly");
  const std = site?.standard_pricing || {};
  const [tier, setTier] = useState(initialTier && TIER_META[initialTier] ? initialTier : "");
  const [hours, setHours] = useState(3);
  const [frequency, setFrequency] = useState("One-off");
  const [props, setProps] = useState([]);
  const [propId, setPropId] = useState("");
  const [addr, setAddr] = useState({ address: "", postcode: "", access_info: "", pets: "" });
  const [date, setDate] = useState("");
  const [win, setWin] = useState("10:00–12:00");
  const [slots, setSlots] = useState(null);
  const [cats, setCats] = useState([]);
  const [qty, setQty] = useState({});
  const [applyCredits, setApplyCredits] = useState(false);
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/portal/properties").then(({ data }) => setProps(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/specialist/services").then(({ data }) => setCats(data.categories || [])).catch(() => {}); }, []);
  useEffect(() => { if (tier && TIER_META[tier]) setHours((h) => Math.max(h, TIER_META[tier].min)); }, [tier]);

  const specItems = Object.entries(qty).filter(([, q]) => q > 0).map(([key, q]) => ({ key, quantity: q }));

  const loadAvailability = async (d) => {
    if (!d) { setSlots(null); return; }
    try {
      const pc = propId ? (props.find((p) => p.id === propId)?.postcode || "") : addr.postcode;
      const { data } = await api.get("/availability", { params: { date: d, service: tier, postcode: pc || undefined } });
      setSlots(data.windows);
      const free = data.windows.find((w) => w.available);
      if (free) setWin(free.window);
    } catch { setSlots(null); }
  };

  useEffect(() => {
    if (!tier) { setQuote(null); return; }
    const body = { tier, hours, frequency, date: date || "2099-01-01", arrival_window: win,
      property_id: propId || null, ...addr, specialist_items: specItems };
    api.post("/portal/book/quote", body).then(({ data }) => setQuote(data)).catch(() => setQuote(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tier, hours, frequency, date, win, propId, JSON.stringify(qty), addr.postcode]);

  const bump = (key, d) => setQty((p) => ({ ...p, [key]: Math.max(0, (p[key] || 0) + d) }));

  const book = async () => {
    if (!tier) return toast.error("Choose a cleaning tier");
    if (!date) return toast.error("Choose a date");
    setBusy(true);
    try {
      const body = { tier, hours, frequency, date, arrival_window: win, property_id: propId || null, ...addr,
        specialist_items: specItems, apply_credits: applyCredits };
      const { data } = await api.post("/portal/book", body);
      if (data.requires_quote) { toast.success(`Quotation requested (${data.reference}). We'll confirm your price before any payment.`); onBooked?.(); return; }
      if (data.paid_in_full) { toast.success(`Booked & paid with credits (${data.reference}). A cleaner is being allocated.`); onBooked?.(); return; }
      const { data: co } = await api.post("/payments/booking-checkout", { booking_id: data.booking_id, origin_url: window.location.origin });
      window.location.href = co.checkout_url;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setBusy(false); }
  };

  const meta = TIER_META[tier];
  const credits = wallet?.credits || 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="booking-flow">
      <div className="lg:col-span-2 space-y-5">
        <div className="card-luxe p-5">
          <p className="text-[11px] tracking-luxe uppercase text-[#b5952f] mb-3">1 · Choose your clean</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {["Essential", "Premium", "Elite Signature"].map((name) => {
              const m = TIER_META[name];
              const rate = std[m.skill === "Premium" ? "Premium" : m.skill === "Elite" ? "Elite" : "Essential"] || m.rate;
              return (
                <button key={name} data-testid={`tier-${name.replace(/ /g, "-")}`} onClick={() => setTier(name)}
                  className={`text-left p-4 rounded-sm border transition-colors ${tier === name ? "border-[#d4af37] bg-[#faf7ef]" : "border-[#e0ddd4] hover:border-[#d4af37]"}`}>
                  <p className="font-display text-lg">{name}</p>
                  <p className="text-[#b5952f] font-display text-2xl mt-1">£{rate}<span className="text-xs text-[#8a8a8a]">/hr</span></p>
                  <p className="text-xs text-[#8a8a8a] mt-1">Min {m.min} hours</p>
                </button>
              );
            })}
          </div>
          <div className="mt-4 bg-[#faf9f5] border border-[#e8e5de] rounded-sm p-3 text-xs text-[#555]">
            Need a fully bespoke programme? <a href="/quote" className="text-[#b5952f] underline">Request a Diamond Elite Ultra quotation →</a>
          </div>
        </div>

        {tier && (
          <>
            <div className="card-luxe p-5">
              <p className="text-[11px] tracking-luxe uppercase text-[#b5952f] mb-3">2 · Duration & frequency</p>
              <div className="flex flex-wrap items-center gap-6">
                <div>
                  <label className="text-xs text-[#555] block mb-1.5">Hours (min {meta.min})</label>
                  <div className="flex items-center gap-2">
                    <button data-testid="hours-minus" onClick={() => setHours((h) => Math.max(meta.min, h - 1))} className="w-8 h-8 rounded-sm border border-[#e0ddd4]"><Minus size={14} className="mx-auto" /></button>
                    <span className="w-8 text-center font-semibold" data-testid="hours-value">{hours}</span>
                    <button data-testid="hours-plus" onClick={() => setHours((h) => h + 1)} className="w-8 h-8 rounded-sm border border-[#e0ddd4]"><Plus size={14} className="mx-auto" /></button>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#555] block mb-1.5">Frequency</label>
                  <select data-testid="book-frequency" value={frequency} onChange={(e) => setFrequency(e.target.value)} className="border border-[#e0ddd4] rounded-sm px-3 py-2 text-sm">
                    {FREQS.map((f) => <option key={f}>{f}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="card-luxe p-5">
              <p className="text-[11px] tracking-luxe uppercase text-[#b5952f] mb-3">3 · Property & access</p>
              {props.length > 0 && (
                <select data-testid="book-property" value={propId} onChange={(e) => setPropId(e.target.value)} className="w-full border border-[#e0ddd4] rounded-sm px-3 py-2 text-sm mb-3">
                  <option value="">New address…</option>
                  {props.map((p) => <option key={p.id} value={p.id}>{p.label} — {p.postcode}</option>)}
                </select>
              )}
              {!propId && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input data-testid="book-address" placeholder="Address" value={addr.address} onChange={(e) => setAddr({ ...addr, address: e.target.value })} className="border border-[#e0ddd4] rounded-sm px-3 py-2 text-sm" />
                  <input data-testid="book-postcode" placeholder="Postcode" value={addr.postcode} onChange={(e) => setAddr({ ...addr, postcode: e.target.value })} className="border border-[#e0ddd4] rounded-sm px-3 py-2 text-sm" />
                  <input data-testid="book-access" placeholder="Access info (e.g. key safe)" value={addr.access_info} onChange={(e) => setAddr({ ...addr, access_info: e.target.value })} className="border border-[#e0ddd4] rounded-sm px-3 py-2 text-sm" />
                  <input data-testid="book-pets" placeholder="Pets" value={addr.pets} onChange={(e) => setAddr({ ...addr, pets: e.target.value })} className="border border-[#e0ddd4] rounded-sm px-3 py-2 text-sm" />
                </div>
              )}
            </div>

            <div className="card-luxe p-5">
              <p className="text-[11px] tracking-luxe uppercase text-[#b5952f] mb-3">4 · Date & arrival window</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input type="date" data-testid="book-date" value={date} onChange={(e) => { setDate(e.target.value); loadAvailability(e.target.value); }} className="border border-[#e0ddd4] rounded-sm px-3 py-2 text-sm" />
                <select data-testid="book-window" value={win} onChange={(e) => setWin(e.target.value)} className="border border-[#e0ddd4] rounded-sm px-3 py-2 text-sm">
                  {(slots || WINDOWS.map((w) => ({ window: w, available: true }))).map((s) => (
                    <option key={s.window} value={s.window} disabled={slots && !s.available}>{s.window}{slots && !s.available ? " — fully booked" : ""}</option>
                  ))}
                </select>
              </div>
              {slots && <p data-testid="book-availability-note" className="text-[11px] mt-2 text-[#b5952f]">{slots.some((s) => s.available) ? "Live availability — we verify a qualified cleaner before confirming." : "No capacity on this date — please choose another."}</p>}
            </div>

            <div className="card-luxe p-5">
              <p className="text-[11px] tracking-luxe uppercase text-[#b5952f] mb-3">5 · Add specialist services (optional)</p>
              <div className="space-y-4 max-h-72 overflow-y-auto pr-1">
                {cats.map((c) => (
                  <div key={c.category}>
                    <p className="text-xs font-semibold text-[#555] mb-1">{c.category}</p>
                    {c.items.map((it) => (
                      <div key={it.key} className="flex justify-between items-center border-b border-[#f0efe9] py-1.5">
                        <span className="text-sm">{it.name} <span className="text-xs text-[#8a8a8a]">{it.unit === "quote" || it.from_price ? `from £${it.price} · quote` : it.unit === "per_hour" ? `£${it.price}/hr` : `£${it.price}`}</span></span>
                        <div className="flex items-center gap-2">
                          <button data-testid={`book-plus-${it.key}`} onClick={() => bump(it.key, -1)} className="w-6 h-6 rounded-sm border border-[#e0ddd4]"><Minus size={11} className="mx-auto" /></button>
                          <span className="w-5 text-center text-xs">{qty[it.key] || 0}</span>
                          <button onClick={() => bump(it.key, 1)} className="w-6 h-6 rounded-sm border border-[#e0ddd4]"><Plus size={11} className="mx-auto" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="card-luxe p-5 h-fit sticky top-24" data-testid="booking-summary">
        <h3 className="font-display text-xl mb-3">Order summary</h3>
        {!quote ? <p className="text-[#8a8a8a] text-sm">Choose a tier to see your itemised total.</p> : <>
          <div className="space-y-1.5 mb-3">
            {quote.lines.map((l, i) => <div key={i} className="flex justify-between text-sm"><span>{l.name}</span><span>{l.amount != null ? `£${l.amount}` : "Quote"}</span></div>)}
          </div>
          <div className="flex justify-between font-semibold border-t border-[#e8e5de] pt-2"><span>{quote.requires_quote ? "Fixed items" : "Total"}</span><span data-testid="booking-total">£{quote.total}</span></div>
          <p className="text-xs text-[#8a8a8a] mt-2">Est. {quote.est_hours}h{quote.required_skills?.length ? ` · skills: ${quote.required_skills.join(", ")}` : ""}</p>
          {quote.requires_quote && <p className="text-xs text-[#b5952f] mt-2">Some items need a quotation — you'll approve the final price before paying.</p>}
          {date && quote.availability && !quote.availability.bookable && <p className="text-xs text-red-500 mt-2">No qualified cleaner for this slot — please pick another date/time.</p>}
          {credits > 0 && !quote.requires_quote && (
            <label className="flex items-center gap-2 mt-3 text-sm" data-testid="apply-credits-toggle">
              <input type="checkbox" checked={applyCredits} onChange={(e) => setApplyCredits(e.target.checked)} />
              Use my {credits} credits (£{wallet.value_gbp}) toward this booking
            </label>
          )}
        </>}
        <button data-testid="booking-submit" disabled={busy || !tier} onClick={book} className="btn-gold w-full py-3 rounded-sm text-sm mt-4 disabled:opacity-50">
          {busy ? "Processing…" : quote?.requires_quote ? "Request quote" : "Book & pay securely"}
        </button>
        <p className="text-[10px] text-[#8a8a8a] mt-2 text-center">Secure card payment via Stripe (test mode). Cancel free up to 48h before.</p>
      </div>
    </div>
  );
}

function PropertiesManager() {
  const [props, setProps] = useState([]);
  const [f, setF] = useState({ label: "", address: "", postcode: "", access_info: "", pets: "" });
  const load = () => api.get("/portal/properties").then(({ data }) => setProps(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!f.label) return toast.error("Give the property a label (e.g. Home)");
    try { await api.post("/portal/properties", f); setF({ label: "", address: "", postcode: "", access_info: "", pets: "" }); toast.success("Property saved"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/portal/properties/${id}`); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  return (
    <div className="space-y-5" data-testid="properties-manager">
      <div className="card-luxe p-6">
        <h3 className="font-display text-2xl mb-4">Add a property</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input data-testid="prop-label" placeholder="Label (e.g. Home, Flat 2)" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} className="border border-[#e0ddd4] rounded-sm px-3 py-2 text-sm" />
          <input data-testid="prop-postcode" placeholder="Postcode" value={f.postcode} onChange={(e) => setF({ ...f, postcode: e.target.value })} className="border border-[#e0ddd4] rounded-sm px-3 py-2 text-sm" />
          <input data-testid="prop-address" placeholder="Address" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} className="border border-[#e0ddd4] rounded-sm px-3 py-2 text-sm sm:col-span-2" />
          <input data-testid="prop-access" placeholder="Access info" value={f.access_info} onChange={(e) => setF({ ...f, access_info: e.target.value })} className="border border-[#e0ddd4] rounded-sm px-3 py-2 text-sm" />
          <input data-testid="prop-pets" placeholder="Pets" value={f.pets} onChange={(e) => setF({ ...f, pets: e.target.value })} className="border border-[#e0ddd4] rounded-sm px-3 py-2 text-sm" />
        </div>
        <button data-testid="prop-save" onClick={add} className="btn-gold px-6 py-2.5 rounded-sm text-sm mt-4">Save property</button>
      </div>
      <div className="space-y-3">
        {props.length === 0 && <Empty text="No saved properties yet." />}
        {props.map((p) => (
          <div key={p.id} className="card-luxe p-5 flex justify-between items-center" data-testid={`prop-${p.id}`}>
            <div><p className="font-semibold">{p.label}</p><p className="text-sm text-[#555]">{p.address} {p.postcode}</p>{p.access_info && <p className="text-xs text-[#8a8a8a]">Access: {p.access_info}</p>}</div>
            <button data-testid={`prop-del-${p.id}`} onClick={() => del(p.id)} className="text-red-500 text-sm">Remove</button>
          </div>
        ))}
      </div>
    </div>
  );
}
